import React, { useState, useEffect } from 'react';
import { Drawer } from './ui/Drawer';
import { ProNumberLink as DefaultProNumberLink } from '../utils/orderCardHelpers';
import { useViewedPros } from '../contexts/ViewedProsContext';
import Toggle from './Toggle';
import ordersService from '../services/orders.service';
import proDocumentsService from '../services/proDocuments.service';
import type { ProDriverPay } from '../services/proDocuments.service';
import { isBelowMarginLimit } from '../utils/marginLimit';
import { WORKING_CATEGORIES } from '../constants/workingCategories';

// Per-leg mile-allocated margin is hidden for now (matches the Pro 360 LegsGrid
// change). Flip to true to re-surface the per-leg "Margin" cell; the live pay
// plumbing stays wired. The per-Pro header margin badge is unaffected.
const SHOW_LEG_MARGIN = false;

interface LegData {
  pronumber: string;
  legrecno?: number;
  legnum: number;
  legtype?: string;
  legaction2?: string;
  legstatus?: string;
  isComplete?: boolean;
  legdrop?: string;
  origcode?: number;
  destcode?: number;
  orig_name?: string;
  orig_address?: string;
  orig_city_state?: string;
  dest_name?: string;
  dest_address?: string;
  dest_city_state?: string;
  origapptdate?: string;
  origappttime1?: string;
  origappttime2?: string;
  destapptdate?: string;
  destappttime1?: string;
  destappttime2?: string;
  container?: string;
  dispatchstatus?: string;
  legmiles?: number;
  estimateddriverpay?: number;
  [key: string]: any;
}

interface ProGroup {
  pronumber: string;
  customer?: string;
  legs: LegData[];
}

interface ProDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  driver: string;
  driverName: string;
  date: string;
  legs: LegData[];
  loading: boolean;
  region?: string;
  initialConfirmed?: boolean;
  changedBy?: string;
  changedDateTime?: string;
  onClearStatus?: (driver: string, date: string) => void;
  hasExistingStatus?: boolean;
  initialComment?: string;
  initialStatus?: string | null;
  initialWorkingCategory?: string | null;
  showStatusSelector?: boolean;
  onStatusSave?: (status: string | null, workingCategory: string | null, comment: string, confirmed: boolean) => Promise<void>;
  onProClick?: (pronumber: number) => void;
  noBackdrop?: boolean;
}

export const ProDetailsDrawer: React.FC<ProDetailsDrawerProps> = ({
  isOpen,
  onClose,
  driver,
  driverName,
  date,
  legs,
  loading,
  initialConfirmed,
  changedBy,
  changedDateTime,
  onClearStatus,
  hasExistingStatus,
  initialComment,
  initialStatus,
  initialWorkingCategory,
  showStatusSelector,
  onStatusSave,
  onProClick,
  noBackdrop,
}) => {
  const { isViewed, markViewed } = useViewedPros();
  const normalizeStatus = (s?: string | null): 'WORKING' | 'NOT_WORKING' | 'UNKNOWN' => {
    const upper = (s ?? '').toUpperCase();
    // Backend overrides WORKING/Available to Planned / Planned Accepted when work is assigned
    // (see drivers.py status/weekly), so those derive from a working driverstatus.
    if (upper === 'WORKING' || upper === 'AVAILABLE' || upper === 'PLANNED' || upper === 'PLANNED ACCEPTED') return 'WORKING';
    if (upper === 'NOT WORKING') return 'NOT_WORKING';
    return 'UNKNOWN';
  };

  // Status selector state
  const [selectedStatus, setSelectedStatus] = useState<'WORKING' | 'NOT_WORKING' | 'UNKNOWN'>(normalizeStatus(initialStatus));
  const [statusWorkingCategory, setStatusWorkingCategory] = useState(initialWorkingCategory ?? '');
  const [statusConfirmed, setStatusConfirmed] = useState(initialConfirmed ?? false);
  const [statusComment, setStatusComment] = useState(initialComment || '');
  const [statusSaving, setStatusSaving] = useState(false);

  // Send to gwiMove state
  const [sendingPro, setSendingPro] = useState<string | null>(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendResult, setSendResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Live per-leg pay + saved margin (POST /pros/driver-pay). Keyed by pronumber string
  // (JSON object keys are strings), matching leg.pronumber / proGroup.pronumber.
  const [margins, setMargins] = useState<Record<string, ProDriverPay>>({});
  const [marginsLoading, setMarginsLoading] = useState(false);

  // Fetch margins when the drawer opens (or its legs change).
  useEffect(() => {
    if (!isOpen || legs.length === 0) {
      setMargins({});
      setMarginsLoading(false);
      return;
    }
    let cancelled = false;
    const pronumbers = [...new Set(legs.map(l => Number(l.pronumber)))];
    setMarginsLoading(true);
    proDocumentsService.getDriverPay(pronumbers)
      .then(result => {
        if (!cancelled) setMargins(result as Record<string, ProDriverPay>);
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to load per-leg margins:', err);
          setMargins({});
        }
      })
      .finally(() => {
        if (!cancelled) setMarginsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, legs]);

  // Sync state with props when drawer opens
  useEffect(() => {
    setStatusConfirmed(initialConfirmed ?? false);
    setStatusComment(initialComment || '');
    setSelectedStatus(normalizeStatus(initialStatus));
    setStatusWorkingCategory(initialWorkingCategory ?? '');
  }, [initialConfirmed, initialComment, initialStatus, initialWorkingCategory]);

  const handleStatusSave = async () => {
    if (!onStatusSave) return;
    setStatusSaving(true);
    try {
      const status = selectedStatus === 'UNKNOWN' ? null : (selectedStatus === 'WORKING' ? 'WORKING' : 'NOT WORKING');
      const category = selectedStatus === 'WORKING' ? (statusWorkingCategory || null) : null;
      await onStatusSave(status, category, statusComment.trim() || ' ', selectedStatus === 'WORKING' ? statusConfirmed : false);
    } catch (error) {
      console.error('Error saving driver status:', error);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleSendToGwiMove = async (proGroup: ProGroup) => {
    const notSentLegs = proGroup.legs.filter(l => l.dispatchstatus === 'Not Sent');
    if (notSentLegs.length === 0) return;

    setSendingPro(proGroup.pronumber);
    setSendResult(null);
    try {
      const items = notSentLegs.map(leg => ({
        unitNumber: driver,
        proNumber: Number(leg.pronumber),
        legrecno: leg.legrecno!,
        legnum: leg.legnum,
      }));
      const response = await ordersService.sendToGwiMove(items);
      setSendResult({ type: 'success', message: response.message });
      // Update dispatch status locally so badge updates without re-fetching
      notSentLegs.forEach(leg => { leg.dispatchstatus = 'Not Accepted'; });
    } catch (err: any) {
      setSendResult({ type: 'error', message: err.response?.data?.detail || err.message || 'Failed to send to gwiMove' });
    } finally {
      setSendingPro(null);
    }
  };

  const handleSendAllToGwiMove = async (proGroups: ProGroup[]) => {
    const allNotSentLegs = proGroups.flatMap(pg => pg.legs.filter(l => l.dispatchstatus === 'Not Sent'));
    if (allNotSentLegs.length === 0) return;

    setSendingAll(true);
    setSendResult(null);
    try {
      const items = allNotSentLegs.map(leg => ({
        unitNumber: driver,
        proNumber: Number(leg.pronumber),
        legrecno: leg.legrecno!,
        legnum: leg.legnum,
      }));
      const response = await ordersService.sendToGwiMove(items);
      setSendResult({ type: 'success', message: response.message });
      allNotSentLegs.forEach(leg => { leg.dispatchstatus = 'Not Accepted'; });
    } catch (err: any) {
      setSendResult({ type: 'error', message: err.response?.data?.detail || err.message || 'Failed to send to gwiMove' });
    } finally {
      setSendingAll(false);
    }
  };

  // Group legs by PRO number, splitting on gaps or drop legs
  const groupedByPro: ProGroup[] = React.useMemo(() => {
    // First, group all legs by PRO number
    const proMap = new Map<string, LegData[]>();

    legs.forEach(leg => {
      const proNum = leg.pronumber;
      if (!proMap.has(proNum)) {
        proMap.set(proNum, []);
      }
      proMap.get(proNum)!.push(leg);
    });

    // Sort legs within each PRO by leg number
    proMap.forEach(proLegs => {
      proLegs.sort((a, b) => a.legnum - b.legnum);
    });

    // Now split into visual groups based on gaps or drop legs
    const result: ProGroup[] = [];

    proMap.forEach((proLegs, pronumber) => {
      let currentGroup: ProGroup = {
        pronumber,
        customer: proLegs[0]?.customer,
        legs: []
      };

      for (let i = 0; i < proLegs.length; i++) {
        const leg = proLegs[i];
        const prevLeg = i > 0 ? proLegs[i - 1] : null;

        // Start a new group if:
        // 1. There's a gap in leg numbers (previous leg + 1 !== current leg)
        // 2. Previous leg was a drop (legdrop === 'Y')
        const hasGap = prevLeg && leg.legnum !== prevLeg.legnum + 1;
        const prevWasDrop = prevLeg && prevLeg.legdrop === 'Y';

        if ((hasGap || prevWasDrop) && currentGroup.legs.length > 0) {
          // Save current group and start a new one
          result.push(currentGroup);
          currentGroup = {
            pronumber,
            customer: leg.customer,
            legs: []
          };
        }

        currentGroup.legs.push(leg);
      }

      // Don't forget the last group
      if (currentGroup.legs.length > 0) {
        result.push(currentGroup);
      }
    });

    return result;
  }, [legs]);

  const formatTimeRange = (time1?: string, time2?: string) => {
    if (!time1 && !time2) return '';
    return time1 && time2 ? `${time1} - ${time2}` : time1 || time2 || '';
  };

  const formattedDate = date ? new Date(date + 'T00:00:00').toLocaleDateString() : '';

  const getStatusBadgeClasses = (status?: string) => {
    switch (status) {
      case 'Accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'Not Sent':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'Not Accepted':
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  // Red when the Pro's total margin is under the configured limit, green otherwise.
  const getMarginBadgeClasses = (underLimit: boolean) =>
    underLimit
      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Driver Legs - ${driver} ${driverName} - ${formattedDate}`}
      width="700px"
      noBackdrop={noBackdrop}
    >
      {/* Driver Status Selector - always visible when enabled */}
      {showStatusSelector && onStatusSave && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-3">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
            Driver Status
          </h3>
          <div className="space-y-2">
            <span className="isolate inline-flex rounded-md shadow-sm w-full">
              <button
                type="button"
                onClick={() => setSelectedStatus('NOT_WORKING')}
                className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold rounded-l-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  selectedStatus === 'NOT_WORKING'
                    ? 'bg-info text-info-fg ring-blue-200'
                    : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                }`}
              >
                Not Working
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('UNKNOWN')}
                className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                  selectedStatus === 'UNKNOWN'
                    ? 'bg-gray-200 text-gray-700 ring-gray-300'
                    : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                }`}
              >
                Unknown
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('WORKING')}
                className={`relative inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-semibold -ml-px rounded-r-md ring-1 ring-inset focus:z-10 focus:outline-none focus:ring-2 focus:ring-green-400 ${
                  selectedStatus === 'WORKING'
                    ? 'bg-green-100 text-green-700 ring-green-200'
                    : 'bg-panel text-muted ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                }`}
              >
                Working
              </button>
            </span>
            {selectedStatus === 'WORKING' && (
              <div>
                <label className="block text-sm font-medium text-label mb-1">
                  Working Category
                </label>
                <select
                  value={statusWorkingCategory}
                  onChange={(e) => setStatusWorkingCategory(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-white"
                >
                  <option value="">Select working category (optional)</option>
                  {WORKING_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            )}
            {selectedStatus === 'WORKING' && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-label">
                  Confirmed
                </span>
                <Toggle
                  checked={statusConfirmed}
                  onChange={setStatusConfirmed}
                  size="md"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-label mb-1">
                Comments
              </label>
              <textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Optional comments..."
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleStatusSave}
                disabled={statusSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {statusSaving ? 'Saving...' : 'Save Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to gwiMove result message */}
      {sendResult && (
        <div className={`mb-3 p-3 rounded-lg text-sm flex items-start ${
          sendResult.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400'
            : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400'
        }`}>
          <span className="flex-1">{sendResult.message}</span>
          <button onClick={() => setSendResult(null)} className="ml-2 font-bold">×</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2 text-label">Loading legs...</span>
        </div>
      ) : groupedByPro.length === 0 ? (
        <div className="text-center py-8 text-muted">
          No legs found for this driver on this date.
        </div>
      ) : (
        <div className="space-y-3">
          {/* Send All To gwiMove button - only show if any legs across all groups are Not Sent */}
          {groupedByPro.some(pg => pg.legs.some(l => l.dispatchstatus === 'Not Sent')) && (
            <button
              onClick={() => handleSendAllToGwiMove(groupedByPro)}
              disabled={sendingAll || sendingPro !== null}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingAll ? 'Sending All...' : 'Send All To gwiMove'}
            </button>
          )}
          {groupedByPro.map((proGroup) => (
            <div
              key={proGroup.pronumber}
              className="border border-gray-300 rounded-lg overflow-hidden dark:border-gray-600"
            >
              {/* PRO Header */}
              <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 border-b border-gray-300 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold text-heading flex items-center gap-2">
                      <span>PRO #</span>
                      {onProClick ? (
                        (() => {
                          const viewed = isViewed(proGroup.pronumber);
                          return (
                            <button
                              type="button"
                              className={`text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer transition-colors duration-150 ${
                                viewed ? 'bg-green-200 dark:bg-green-600/50 px-1 rounded' : ''
                              }`}
                              title={viewed ? 'Viewed' : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                markViewed(proGroup.pronumber);
                                onProClick(Number(proGroup.pronumber));
                              }}
                            >
                              {proGroup.pronumber}
                            </button>
                          );
                        })()
                      ) : (
                        <DefaultProNumberLink pronumber={proGroup.pronumber} />
                      )}
                    </div>
                    {proGroup.customer && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {proGroup.customer}
                      </div>
                    )}
                    {(proGroup.legs[0]?.pregatepin || proGroup.legs[0]?.pregatechassis) && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">PIN / Pregate Chass:</span> {[proGroup.legs[0].pregatepin, proGroup.legs[0].pregatechassis].filter(Boolean).join(' / ')}
                      </div>
                    )}
                    {(proGroup.legs[0]?.emptypregatepin || proGroup.legs[0]?.emptypregatechassis) && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Empty PIN / Empty Pregate Chass:</span> {[proGroup.legs[0].emptypregatepin, proGroup.legs[0].emptypregatechassis].filter(Boolean).join(' / ')}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {(() => {
                      const hm = margins[String(proGroup.pronumber)];
                      if (hm?.savedMarginPct == null) return null;
                      return (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMarginBadgeClasses(isBelowMarginLimit(hm.savedMarginPct, hm.marginLimit))}`}
                          title="Total margin (customer revenue vs. driver + additional costs)"
                        >
                          Margin {hm.savedMarginPct.toFixed(1)}%
                        </span>
                      );
                    })()}
                    <div className="text-sm text-muted">
                      {proGroup.legs.length} leg{proGroup.legs.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Legs */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {proGroup.legs.map((leg) => {
                  // Live per-leg pay/margin, matched by legrecno within the Pro's margin.
                  const m = margins[String(leg.pronumber)];
                  const pl = m?.driverPay.legs.find(x => x.legrecno === leg.legrecno);
                  return (
                  <div
                    key={leg.legnum}
                    className="p-4 bg-panel hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className={`text-sm font-medium ${
                        leg.isComplete
                          ? 'bg-slate-800 text-white px-2 py-0.5 rounded'
                          : 'text-heading'
                      }`}>
                        Leg #{leg.legnum}
                      </div>
                      {leg.dispatchstatus && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(leg.dispatchstatus)}`}>
                          {leg.dispatchstatus}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-4">
                      {/* Left side: Origin, Destination, Container */}
                      <div className="flex-1 space-y-2">
                        {/* Origin and Destination side by side */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Origin - labeled with legtype */}
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-muted uppercase">
                              {leg.legtype || 'Origin'}
                            </div>
                            <div
                              className={`text-sm font-semibold text-gray-900 dark:text-white ${leg.orig_address ? 'cursor-help' : ''}`}
                              title={leg.orig_address || undefined}
                            >
                              {leg.orig_name || 'Not specified'}
                            </div>
                            {leg.orig_city_state && (
                              <div className="text-xs text-muted">
                                {leg.orig_city_state}
                              </div>
                            )}
                          </div>

                          {/* Destination - labeled with legaction2 */}
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-muted uppercase">
                              {leg.legaction2 || 'Destination'}
                            </div>
                            <div
                              className={`text-sm font-semibold text-gray-900 dark:text-white ${leg.dest_address ? 'cursor-help' : ''}`}
                              title={leg.dest_address || undefined}
                            >
                              {leg.dest_name || 'Not specified'}
                            </div>
                            {leg.dest_city_state && (
                              <div className="text-xs text-muted">
                                {leg.dest_city_state}
                              </div>
                            )}
                            {/* Only show appt time for Delivery legs */}
                            {leg.legaction2?.toLowerCase() === 'delivery' && formatTimeRange(leg.destappttime1, leg.destappttime2) && (
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                {formatTimeRange(leg.destappttime1, leg.destappttime2)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Container */}
                        {leg.container && (
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-muted uppercase">
                              Container
                            </div>
                            <div className="text-sm text-heading">
                              {leg.container}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right side: Miles, Pay, Margin */}
                      <div className="w-24 space-y-2 text-right">
                        {leg.legmiles != null && leg.legmiles > 0 && (
                          <div>
                            <div className="text-xs font-medium text-muted uppercase">
                              Miles
                            </div>
                            <div className="text-sm text-heading">
                              {leg.legmiles}
                            </div>
                          </div>
                        )}
                        {/* Est. Pay: live legPay when available, else fall back to
                            the row's estimateddriverpay (e.g. while margins load). */}
                        {(pl || (leg.estimateddriverpay != null && leg.estimateddriverpay > 0)) && (
                          <div>
                            <div
                              className="text-xs font-medium text-muted uppercase"
                              title={pl?.isPaid && pl.state === 'OK' && pl.payrollDate ? `WE ${pl.payrollDate}` : undefined}
                            >
                              {pl?.isPaid && pl.state === 'OK' ? 'Pay' : 'Est. Pay'}
                            </div>
                            {pl ? (
                              pl.state === 'OK' ? (
                                <div className="text-sm text-heading">
                                  ${pl.legPay.toFixed(2)}
                                </div>
                              ) : (
                                <div className="text-sm font-medium text-red-600 dark:text-red-400">
                                  {pl.state}
                                </div>
                              )
                            ) : (
                              <div className="text-sm text-heading">
                                ${leg.estimateddriverpay!.toFixed(2)}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Margin (mile-allocated): hidden for now — see SHOW_LEG_MARGIN. */}
                        {SHOW_LEG_MARGIN && pl && (
                          <div>
                            <div className="text-xs font-medium text-muted uppercase">
                              Margin
                            </div>
                            <div className="text-sm text-heading">
                              {pl.marginPct == null ? 'N/A' : `${pl.marginPct.toFixed(1)}%`}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Send To gwiMove button - only show if any legs are Not Sent */}
              {proGroup.legs.some(l => l.dispatchstatus === 'Not Sent') && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleSendToGwiMove(proGroup)}
                    disabled={sendingPro === proGroup.pronumber}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingPro === proGroup.pronumber ? 'Sending...' : 'Send To gwiMove'}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Last changed by note and Clear Status button */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-xs text-muted">
              {changedBy && <>Last changed by: {changedBy}</>}
              {changedBy && changedDateTime && ' - '}
              {changedDateTime && <>{changedDateTime}</>}
            </div>
            {onClearStatus && hasExistingStatus && (
              <button
                onClick={() => onClearStatus(driver, date)}
                className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Clear Status
              </button>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default ProDetailsDrawer;
